import { useEffect, useRef, useState } from 'react'
import type { GlossaryDictionaryListItem } from '@ai-meeting/shared'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentIcon,
  DownloadIcon,
  GripVerticalIcon,
  PencilIcon,
  TrashIcon,
} from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import { formatDate } from '@/utils/formatDateTime'

interface UserDictionaryRowProps {
  dictionary: GlossaryDictionaryListItem
  index: number
  isFirst: boolean
  isLast: boolean
  isDragging: boolean
  isDropTarget: boolean
  onRename: (name: string) => Promise<void>
  onSetDefault: (isDefault: boolean) => Promise<void>
  onMoveUp: () => void
  onMoveDown: () => void
  onDownload: () => void
  onDelete: () => void
  onDragStart: () => void
  onDragEnd: () => void
  onDragOver: (event: React.DragEvent) => void
  onDrop: () => void
}

function formatTermCount(count: number, locale: string): string {
  return count.toLocaleString(locale === 'ja' ? 'ja-JP' : 'en-US')
}

export default function UserDictionaryRow({
  dictionary,
  index,
  isFirst,
  isLast,
  isDragging,
  isDropTarget,
  onRename,
  onSetDefault,
  onMoveUp,
  onMoveDown,
  onDownload,
  onDelete,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: UserDictionaryRowProps) {
  const { t, locale } = useUiStrings()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(dictionary.name)
  const [renameError, setRenameError] = useState<string | null>(null)
  const [defaultPending, setDefaultPending] = useState(false)
  const renameInFlightRef = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const rowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!editing) setDraft(dictionary.name)
  }, [dictionary.name, editing])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  async function commitRename() {
    if (renameInFlightRef.current) return
    const next = draft.trim()
    if (!next || next === dictionary.name) {
      setDraft(dictionary.name)
      setRenameError(null)
      setEditing(false)
      return
    }
    renameInFlightRef.current = true
    setRenameError(null)
    try {
      await onRename(next)
      setEditing(false)
    } catch {
      setRenameError(t.glossaryRenameFailed)
    } finally {
      renameInFlightRef.current = false
    }
  }

  function handleDragStart(event: React.DragEvent) {
    const row = rowRef.current
    if (row) {
      event.dataTransfer.setDragImage(row, 24, row.offsetHeight / 2)
      event.dataTransfer.effectAllowed = 'move'
    }
    onDragStart()
  }

  return (
    <div
      ref={rowRef}
      className={[
        'flex flex-wrap items-center gap-2 rounded-lg border bg-[var(--bg-secondary)] px-3 py-3.5 transition-[opacity,box-shadow,border-color,transform] sm:gap-3 sm:px-4',
        isDragging
          ? 'scale-[0.98] border-[var(--border-color)] opacity-40'
          : isDropTarget
            ? 'border-[var(--accent)] shadow-[0_0_0_2px_var(--accent-light)]'
            : 'border-[var(--border-color)]',
      ].join(' ')}
      onDragOver={onDragOver}
      onDrop={(event) => {
        event.preventDefault()
        onDrop()
      }}
    >
      <button
        type="button"
        draggable
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
        className="-ml-1 cursor-grab touch-manipulation rounded p-2 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-secondary)] active:cursor-grabbing"
        aria-label={t.glossaryDragHandle}
      >
        <GripVerticalIcon className="h-4 w-4" />
      </button>

      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--accent-light)] text-xs font-semibold text-[var(--accent)]">
        {index + 1}
      </span>

      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--accent-light)] text-[var(--accent)]">
        <DocumentIcon className="h-4 w-4" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          {editing ? (
            <input
              ref={inputRef}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value)
                setRenameError(null)
              }}
              onBlur={() => void commitRename()}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void commitRename()
                }
                if (event.key === 'Escape') {
                  setDraft(dictionary.name)
                  setRenameError(null)
                  setEditing(false)
                }
              }}
              className="input-field h-8 min-w-0 flex-1 px-2 text-sm font-semibold"
              aria-label={t.glossaryRename}
            />
          ) : (
            <>
              <p className="truncate text-sm font-semibold text-[var(--text-primary)]">
                {dictionary.name}
              </p>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="shrink-0 rounded p-1 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--accent)]"
                aria-label={t.glossaryRename}
              >
                <PencilIcon className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
        {renameError && (
          <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400" role="alert">
            {renameError}
          </p>
        )}
        <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">
          {formatTermCount(dictionary.termCount, locale)} {t.glossaryTermsLabel} ·{' '}
          {t.glossaryUploadedLabel}{' '}
          {formatDate(new Date(dictionary.uploadedAt).getTime(), locale)}
        </p>
      </div>

      <div className="flex shrink-0 flex-col items-center gap-1 px-1">
        <button
          type="button"
          role="switch"
          aria-checked={dictionary.isDefault}
          aria-label={t.glossaryDefaultLabel}
          disabled={defaultPending}
          onClick={() => {
            if (defaultPending) return
            const next = !dictionary.isDefault
            setDefaultPending(true)
            void onSetDefault(next).finally(() => setDefaultPending(false))
          }}
          className={[
            'relative h-6 w-10 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:cursor-not-allowed disabled:opacity-60',
            dictionary.isDefault ? 'bg-[var(--accent)]' : 'bg-[var(--border-hover)]',
          ].join(' ')}
        >
          <span
            className={[
              'absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
              dictionary.isDefault ? 'translate-x-4' : 'translate-x-0',
            ].join(' ')}
          />
        </button>
        <span className="text-[10px] font-semibold text-[var(--text-tertiary)]">
          {t.glossaryDefaultLabel}
        </span>
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        <div className="flex flex-col">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className="rounded p-0.5 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-30"
            aria-label={t.glossaryMoveUp}
          >
            <ChevronUpIcon className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className="rounded p-0.5 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-30"
            aria-label={t.glossaryMoveDown}
          >
            <ChevronDownIcon className="h-3.5 w-3.5" />
          </button>
        </div>

        <button
          type="button"
          onClick={onDownload}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)]"
          aria-label={t.glossaryDownload}
        >
          <DownloadIcon className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={onDelete}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-500 transition-colors hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-950/40"
          aria-label={t.delete}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
